// The signed-in session provider: sign-in state, the access token, /v1/me,
// and the selected tenant (read with useSession from sessionContext.ts).
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { fetchMe } from './api';
import { readHint, SIGNED_IN_HINT, TENANT_KEY, writeHint } from './auth';
import {
  currentMembership,
  SessionContext,
  type MeState,
  type Session,
  type SigninState,
} from './sessionContext';

export function SessionProvider({
  apiUrl,
  children,
}: {
  apiUrl: string;
  children: React.ReactNode;
}) {
  const auth = useAuth();
  // Results are keyed by the token they were fetched with, so a new token
  // reads as "loading" without resetting state inside the effect.
  const [result, setResult] = useState<{ token: string; state: MeState } | null>(null);
  const [tenantId, setTenantId] = useState<string>(() => readHint(TENANT_KEY) ?? '');
  const token = auth.user?.access_token;

  // After a reload, resume the session through Keycloak if we were signed in.
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && !auth.error && !auth.activeNavigator) {
      if (readHint(SIGNED_IN_HINT) === '1') {
        const state: SigninState = { returnTo: window.location.pathname };
        void auth.signinRedirect({ state });
      }
    }
  }, [auth]);

  useEffect(() => {
    if (!token) return;
    writeHint('session', SIGNED_IN_HINT, '1');
    const controller = new AbortController();
    fetchMe(apiUrl, token, controller.signal)
      .then((data) => {
        setResult({ token, state: { kind: 'ready', me: data } });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          token,
          state: { kind: 'error', error: err instanceof Error ? err.message : String(err) },
        });
      });
    return () => {
      controller.abort();
    };
  }, [apiUrl, token]);

  // A renewed token refetches /v1/me; meanwhile keep showing the last good
  // answer so pages (and their live sockets) aren't torn down.
  const me: MeState = !token
    ? { kind: 'idle' }
    : result?.token === token || result?.state.kind === 'ready'
      ? result.state
      : { kind: 'loading' };
  const membership = currentMembership(me, tenantId);

  const setTenant = useCallback((id: string) => {
    setTenantId(id);
    writeHint('local', TENANT_KEY, id);
  }, []);
  const signIn = useCallback(() => {
    const state: SigninState = { returnTo: window.location.pathname };
    void auth.signinRedirect({ state });
  }, [auth]);
  const signOut = useCallback(() => {
    writeHint('session', SIGNED_IN_HINT, null);
    void auth.signoutRedirect();
  }, [auth]);
  const reauthenticate = useCallback(
    (returnTo: string) => {
      const state: SigninState = { returnTo };
      void auth.signinRedirect({ prompt: 'login', state });
    },
    [auth],
  );

  const email = auth.user?.profile.email ?? auth.user?.profile.sub;
  const value: Session = {
    apiUrl,
    authenticated: auth.isAuthenticated,
    authLoading: auth.isLoading,
    authError: auth.error ?? undefined,
    email,
    token,
    me,
    membership,
    permissions: membership?.permissions ?? [],
    setTenant,
    signIn,
    signOut,
    reauthenticate,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
